import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import PageMeta from "@/components/PageMeta";
import { IMAGES, ORG } from "@/lib/constants";
import { Mail, ArrowUpRight } from "lucide-react";

// Last updated date for the policy. Edit this string when content changes.
const LAST_UPDATED = "May 19, 2026";

function Section({ heading, id, children }) {
  return (
    <section className="mt-10 lg:mt-12" data-testid={`privacy-${id}`}>
      <h2 className="font-display text-2xl lg:text-3xl text-white">{heading}</h2>
      <div className="mt-4 space-y-4 text-slate-300 leading-relaxed text-[15px] lg:text-base">
        {children}
      </div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <PageMeta
        title="Privacy Policy"
        description="How the Phoenix of the Detroit Fire Department handles information collected through this website — contact inquiries, newsletter signups, rental inquiries, Zeffy forms, and server logs."
        path="/privacy"
      />

      <PageHero
        overline="Privacy"
        title="Privacy Policy"
        subtitle="The Phoenix of the Detroit Fire Department respects your privacy and limits the information collected on this website to what is needed to respond to your inquiry, share updates you have requested, and support Phoenix programs and events."
        image={IMAGES.detroit}
      />

      <section className="py-12 lg:py-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <p className="text-sm tracking-[0.22em] uppercase font-accent text-amber-500">
            Last updated · {LAST_UPDATED}
          </p>

          <Section heading="Who we are" id="who">
            <p>
              The Phoenix of the Detroit Fire Department is a Michigan-based
              501(c)(3) nonprofit organization. This website is published and
              maintained by Phoenix to share information about our programs,
              membership, Historic Engine 11, the 2027 NCR Spring Conference,
              the Phoenix 60th Anniversary Gala, and related community work.
            </p>
            <p>
              You can reach Phoenix regarding this policy at{" "}
              <a
                href={`mailto:${ORG.email}`}
                className="text-amber-400 hover:text-amber-300 underline-offset-4 hover:underline"
                data-testid="privacy-contact-email"
              >
                {ORG.email}
              </a>{" "}
              or by mail at {ORG.address}.
            </p>
          </Section>

          <Section heading="What we collect" id="what">
            <p>The website collects information only when you choose to share it. Specifically:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-slate-100">Contact inquiries.</strong>{" "}
                When you submit the Contact form, we collect your name and
                email, an optional phone number, the type of inquiry you
                selected, a subject, your message, and any additional fields
                relevant to that inquiry (for example, an organization name for
                sponsorship inquiries, areas of interest for volunteer
                inquiries, or event details for rental inquiries).
              </li>
              <li>
                <strong className="text-slate-100">Newsletter signups.</strong>{" "}
                When you subscribe through the Events &amp; News page, we
                collect your email address.
              </li>
              <li>
                <strong className="text-slate-100">Rental inquiries.</strong>{" "}
                When you submit the Historic Engine 11 rental inquiry form, we
                collect your contact information and the event details you
                provide so Phoenix can follow up with you.
              </li>
              <li>
                <strong className="text-slate-100">Server logs.</strong>{" "}
                Our hosting environment records standard technical information
                such as IP address, browser type, referring URL, and request
                timestamps. This information is used only for diagnostics,
                security, and reliability of the website.
              </li>
            </ul>
            <p>
              The website does not currently use third-party advertising
              trackers, ad networks, behavioral profiling, or third-party
              analytics services. The website does not set non-essential
              cookies.
            </p>
          </Section>

          <Section heading="Forms hosted by Zeffy" id="zeffy">
            <p>
              Phoenix uses Zeffy, an external nonprofit fundraising and event
              platform, to handle certain interest, sponsorship inquiry, and
              volunteer forms. When you click a Zeffy link from this website,
              you leave the Phoenix site and submit information directly to
              Zeffy. Information submitted to Zeffy is collected, stored, and
              handled by Zeffy according to its own privacy policy, which is
              available at{" "}
              <a
                href="https://www.zeffy.com/privacy-policy"
                target="_blank"
                rel="noreferrer"
                className="text-amber-400 hover:text-amber-300 underline-offset-4 hover:underline inline-flex items-center gap-1"
                data-testid="privacy-zeffy-link"
              >
                www.zeffy.com/privacy-policy <ArrowUpRight className="h-3 w-3" />
              </a>
              . Phoenix receives the contents of Zeffy submissions in order to
              follow up with you, but the underlying technical processing of
              those submissions is performed by Zeffy.
            </p>
          </Section>

          <Section heading="How we use the information" id="use">
            <p>Phoenix uses the information you share through this website to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>respond to your inquiry, including by email or phone;</li>
              <li>
                share Phoenix updates, event announcements, and conference and
                anniversary news with you if you have signed up to receive them;
              </li>
              <li>
                coordinate sponsorships, table reservations, ad journal
                placements, volunteer interest, membership inquiries, and
                rentals of Historic Engine 11;
              </li>
              <li>
                support Phoenix programs, including community outreach, retiree
                fellowship, active and retired firefighter fellowship, future
                firefighter mentorship, and the preservation of Historic Engine
                11; and
              </li>
              <li>maintain the security and reliability of the website.</li>
            </ul>
          </Section>

          <Section heading="How we share information" id="share">
            <p>
              Phoenix does not sell or rent visitor information. We do not
              share information collected through this website with outside
              organizations for advertising or marketing purposes.
            </p>
            <p>
              We may share limited information with service providers we use to
              operate the website and our programs — for example, our hosting
              and database providers, our email service, and Zeffy — only to
              the extent needed to deliver those services. We may also disclose
              information when we believe in good faith that disclosure is
              required by law or necessary to protect the rights, property, or
              safety of Phoenix, its members, or others.
            </p>
          </Section>

          <Section heading="How long we keep information" id="retention">
            <p>
              Phoenix keeps inquiry, newsletter, and rental records for as long
              as reasonably needed to follow up with you and to maintain
              accurate records of Phoenix communications, sponsorships,
              programs, and events. You may ask Phoenix to remove your contact
              information from our records at any time by emailing{" "}
              <a
                href={`mailto:${ORG.email}`}
                className="text-amber-400 hover:text-amber-300 underline-offset-4 hover:underline"
              >
                {ORG.email}
              </a>
              .
            </p>
          </Section>

          <Section heading="Your choices" id="choices">
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-slate-100">Unsubscribing.</strong> You
                can unsubscribe from Phoenix newsletter emails at any time by
                replying to any email or contacting us at {ORG.email}.
              </li>
              <li>
                <strong className="text-slate-100">Accessing or correcting your information.</strong>{" "}
                You may request a summary of the contact information Phoenix
                holds about you, ask us to correct it, or ask us to delete it.
                We will respond to reasonable requests in a reasonable
                timeframe.
              </li>
              <li>
                <strong className="text-slate-100">Children.</strong> This
                website is not directed to children under 13, and Phoenix does
                not knowingly collect information from children under 13.
              </li>
            </ul>
          </Section>

          <Section heading="Security" id="security">
            <p>
              Phoenix takes reasonable steps to protect the information
              collected through this website using common security practices,
              including encrypted connections (HTTPS) and access controls for
              Phoenix administrators. No method of transmission or storage is
              completely secure, however, and Phoenix cannot guarantee absolute
              security of information transmitted over the internet.
            </p>
          </Section>

          <Section heading="Changes to this policy" id="changes">
            <p>
              Phoenix may update this Privacy Policy from time to time to
              reflect changes in the website, our programs, or applicable law.
              The “Last updated” date at the top of this page will reflect the
              date of the most recent update. Material changes will be
              communicated through the website or, where appropriate, by email
              to Phoenix newsletter subscribers.
            </p>
          </Section>

          <Section heading="Contact us about this policy" id="contact">
            <p>
              If you have any questions about this Privacy Policy or about how
              Phoenix handles information collected through the website, please
              email us at{" "}
              <a
                href={`mailto:${ORG.email}`}
                className="text-amber-400 hover:text-amber-300 underline-offset-4 hover:underline"
              >
                {ORG.email}
              </a>
              , write to us at {ORG.address}, or call us at {ORG.phone}.
            </p>
            <p className="text-sm text-slate-500 mt-6">
              This Privacy Policy is provided as general information about how
              Phoenix handles website data. It is not legal advice. Phoenix may
              refine this policy following nonprofit legal review.
            </p>
          </Section>

          <div className="mt-12 flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              className="btn-gold w-full sm:w-auto justify-center"
              data-testid="privacy-contact-cta"
            >
              <Mail className="h-4 w-4" /> Contact Phoenix
            </Link>
            <Link
              to="/terms"
              className="btn-ghost w-full sm:w-auto justify-center"
              data-testid="privacy-terms-cta"
            >
              Read Terms of Use
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
